/**
 * Formats d'export lisibles par des outils standards : GPX 1.1 pour les
 * trajets (QGIS, Google Earth, logiciels de police scientifique), CSV pour
 * les messages et événements (tableur). Fonctions pures, testées hors navigateur.
 */

const GPX_EXTENSION_NS = 'https://monalerte.app/xmlschemas/gpx/1';

export function escapeXml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

const iso = epochMs => new Date(epochMs).toISOString();

/** Un fichier GPX contenant une piste (trk) par participant. */
export function toGpx(bundle, { trackIds = null, generatedAt = Date.now() } = {}) {
    const tracks = (bundle?.tracks || []).filter(track => !trackIds || trackIds.includes(track.id));
    const lines = [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<gpx version="1.1" creator="MonAlerte" xmlns="http://www.topografix.com/GPX/1/1" xmlns:ma="${GPX_EXTENSION_NS}">`,
        '  <metadata>',
        `    <name>${escapeXml(bundle?.title || 'Activité MonAlerte')}</name>`,
        `    <desc>${escapeXml(`Activité ${bundle?.id || ''} — positions transmises par les téléphones des participants`)}</desc>`,
        `    <time>${iso(generatedAt)}</time>`,
        '  </metadata>',
    ];
    tracks.forEach(track => {
        lines.push('  <trk>');
        lines.push(`    <name>${escapeXml(track.label)}</name>`);
        lines.push(`    <desc>${escapeXml(`${track.role || 'Participant'} (identifiant ${track.userId})`)}</desc>`);
        lines.push('    <trkseg>');
        (track.positions || []).forEach(point => {
            lines.push(`      <trkpt lat="${point.lat.toFixed(7)}" lon="${point.lng.toFixed(7)}">`);
            lines.push(`        <time>${iso(point.t)}</time>`);
            const extensions = [];
            if (Number.isFinite(point.accuracy)) extensions.push(`<ma:accuracy>${point.accuracy}</ma:accuracy>`);
            if (point.inZone !== null && point.inZone !== undefined) extensions.push(`<ma:inZone>${point.inZone ? 'true' : 'false'}</ma:inZone>`);
            if (extensions.length) lines.push(`        <extensions>${extensions.join('')}</extensions>`);
            lines.push('      </trkpt>');
        });
        lines.push('    </trkseg>');
        lines.push('  </trk>');
    });
    lines.push('</gpx>');
    return `${lines.join('\n')}\n`;
}

/** Cellule CSV (séparateur « ; » pour un tableur français). */
export function csvCell(value) {
    const text = String(value ?? '');
    return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function toCsv(header, rows) {
    // BOM UTF-8 : sans lui, Excel affiche mal les accents.
    return `﻿${[header, ...rows].map(row => row.map(csvCell).join(';')).join('\r\n')}\r\n`;
}

const localTime = epochMs => new Date(epochMs).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });

export function messagesToCsv(bundle) {
    const labels = new Map((bundle?.tracks || []).map(track => [track.id, track.label]));
    return toCsv(
        ['horodatage_utc', 'heure_locale', 'expediteur', 'participant', 'message'],
        (bundle?.messages || []).map(message => [
            iso(message.t), localTime(message.t), message.senderName, labels.get(message.trackId) || '', message.text,
        ]),
    );
}

export function eventsToCsv(bundle) {
    const labels = new Map((bundle?.tracks || []).map(track => [track.id, track.label]));
    return toCsv(
        ['horodatage_utc', 'heure_locale', 'type', 'description', 'participant'],
        (bundle?.events || []).map(event => [
            iso(event.t), localTime(event.t), event.kind, event.label, labels.get(event.trackId) || '',
        ]),
    );
}

/** Nom de fichier sans caractère problématique. */
export function safeFileName(value, fallback = 'fichier') {
    const cleaned = String(value || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9._-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
    return cleaned || fallback;
}
